import cloneDeep from 'lodash/cloneDeep';
import paper from 'paper';
import svgPath from 'svgpath';
import tinycolor from 'tinycolor2';
import { Layer as ParseLayer } from '@meitu-graphic/ag-psd';
import { parseVector } from '@meitu/saas-editor-core-next/es/utils/vector-kit';
import { DefaultClientVectorLayer } from '@meitu/saas-editor-core-next/es/version-parse/constants';
import {
    StretchOptionType,
    type ILayerProps,
    type IVectorLayerProps,
} from '@meitu/saas-editor-core-next/es/version-types';
import { PsdParseWarnType, UploadImage, WarnOrErrorCallbackFunc } from './types';
import {
    getFourCornerBorderRadius,
    getGradientColor,
    getStrokeGradientColor,
    isEllipse,
    isGradientColor,
    isRect,
} from './utils';

const canvas = document.createElement('canvas');
canvas.width = 8000;
canvas.height = 8000;
const paperCanvas = new paper.Project(canvas);

const getPathInfoFormPath = (path: string) => {
    // 从path字符串中提取所有数字
    const numbers = path.match(/-?\d+\.?\d*/g);
    if (!numbers) return { width: 0, height: 0 };

    // 将所有数字转换为浮点数
    const coords = numbers.map(Number);

    // 初始化最大最小值
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    // path中的数字两两一组代表x,y坐标
    for (let i = 0; i < coords.length; i += 2) {
        const x = coords[i];
        const y = coords[i + 1];

        if (!isNaN(x)) {
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
        }

        if (!isNaN(y)) {
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
        }
    }

    // 计算宽度和高度
    const width = Math.ceil(maxX - minX);
    const height = Math.ceil(maxY - minY);

    return {
        width,
        height,
        minX,
        minY,
        maxX,
        maxY,
    };
};
//todo 多端渐变导出有异常，暂时只支持PS里设置首尾颜色
export const getVectorLayer = async (
    stageWidth: number,
    stageHeight: number,
    parentLayer: ParseLayer,
    layer: ParseLayer,
    uploadFile: UploadImage,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps | null>,
) => {
    const vectorLayer = cloneDeep(DefaultClientVectorLayer);
    const isRectVector = isRect(layer),
        isEllipseVector = isEllipse(layer);
    vectorLayer.opacity = layer.opacity || 1;
    vectorLayer.id = layer.id?.toString() || vectorLayer.id;
    const { top = 0, bottom = 0, left = 0, right = 0, opacity = 1, name: title } = layer;
    vectorLayer.left = left;
    vectorLayer.top = top;
    vectorLayer.opacity = opacity * (layer.fillOpacity || 1);
    vectorLayer.title = title || '';
    vectorLayer.width = right - left;
    vectorLayer.height = bottom - top;
    const vectorStroke = layer.vectorStroke || {};
    const strokeWidth = (() => {
        if (vectorStroke && vectorStroke.strokeEnabled) {
            return vectorStroke?.lineWidth?.value || 0;
        }
        return 0;
    })();

    warnOrErrorCallback?.(`解析矢量图层：${vectorLayer.title}`, PsdParseWarnType.Info);
    vectorLayer.parentId = parentLayer.id && parentLayer.id === layer.id ? '' : parentLayer.id?.toString();
    let vectorData = vectorLayer.data as IVectorLayerProps;

    // 这里引入偏移，解决异形svg描边截断的问题，整体居于中心点外扩
    // 需要修改图层宽高、位置、svg的viewBox、path的translate
    const transDiff = Math.floor(Math.max(vectorLayer.width, vectorLayer.height) / 15);
    vectorLayer.width = vectorLayer.width + transDiff * 2;
    vectorLayer.height = vectorLayer.height + transDiff * 2;
    vectorLayer.left = vectorLayer.left - transDiff;
    vectorLayer.top = vectorLayer.top - transDiff;
    const path = getVectorPath(layer, vectorLayer, transDiff);
    // 添加xml声明，否则上传的svg无法正常识别content-type
    const svgHtml = `<?xml version="1.0" encoding="UTF-8"?><svg id="svg" version="1.1"  viewBox="0 0 ${vectorLayer.width} ${vectorLayer.height}" style="position: absolute;left: 0;top: 0;"  xmlns="http://www.w3.org/2000/svg">
          ${path}
      </svg>`;
    console.log('transDiff svgHtml', transDiff, svgHtml);
    // 当4个圆角值不同时，需要使用borderRadius
    const borderRadius = getFourCornerBorderRadius(layer.vectorOrigination);
    const svgInfo = parseVector(svgHtml);

    vectorData = { ...vectorData, ...svgInfo.info };
    if (borderRadius) {
        vectorData.borderRadius = borderRadius;
    }
    const keyDescriptorList = layer.vectorOrigination?.keyDescriptorList;
    const svgAngleToCssGradient = (svgAngle: number) => {
        const cssAngle = (90 - svgAngle) % 360; //svg坐标 转成css坐标
        return cssAngle;
    };

    if (isGradientColor(layer)) {
        const gradientColor = getGradientColor(layer);
        const { colors, angle } = gradientColor;
        vectorData.fillGradient = {
            colors: colors.map((item: any) => {
                return {
                    ...item,
                    color: tinycolor(item.color).toHex8String(),
                };
            }),
            angle: svgAngleToCssGradient(angle),
        };
        vectorData.colors = [];
    }

    if (isRectVector || isEllipseVector) {
        const gradientColor = getStrokeGradientColor(layer);
        if (gradientColor) {
            const { colors, angle } = gradientColor;
            vectorData.strokeGradient = {
                colors: colors.map((item: any) => {
                    return {
                        ...item,
                        color: tinycolor(item.color).toHex8String(),
                    };
                }),
                angle: svgAngleToCssGradient(angle),
            };
        }
    }

    if (isRectVector || isEllipseVector) {
        vectorData.vectorType = (isRectVector ? 'rect' : 'ellipse') as any;
        if (keyDescriptorList && keyDescriptorList.length === 1 && keyDescriptorList[0]?.keyOriginBoxCorners) {
            const [point0, point1, point2, point3] = keyDescriptorList[0].keyOriginBoxCorners;
            const width = Math.sqrt(Math.pow(point1.x - point0.x, 2) + Math.pow(point1.y - point0.y, 2));
            const height = Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2));
            vectorLayer.width = Math.ceil(width + strokeWidth);
            vectorLayer.height = Math.ceil(height + strokeWidth);
            let rotate = Math.atan2(point1.y - point0.y, point1.x - point0.x) * (180 / Math.PI);
            // 标准化角度到 0-360 范围
            rotate = (rotate + 360) % 360;
            vectorLayer.rotate = rotate;
            // 必须使用round，否则在 psd里图片用智能蒙版时会偏移1px
            vectorLayer.top = Math.round(
                Math.abs(point2.y - point0.y) / 2 + (point2.y < point0.y ? point2.y : point0.y) - height / 2,
            );

            vectorLayer.left = Math.round(
                Math.abs(point2.x - point0.x) / 2 + (point2.x < point0.x ? point2.x : point0.x) - width / 2,
            );
        }
    }

    const svgBlob = new Blob([svgInfo.text], { type: 'image/svg+xml' });
    const { type } = svgBlob;
    const file = new File([svgBlob], '自定义图片', { type });
    const url = await uploadFile(file, 'svg');
    vectorData.url = url;
    // 将形状图层转换成SVG元素并将它们添加到SVG中
    vectorData.stretchOption = StretchOptionType.fill;
    vectorLayer.shine = false;
    if ((vectorLayer.width > stageWidth * 2 || vectorLayer.height > stageHeight * 2) && !parseNoLimit) {
        warnOrErrorCallback?.(`矢量图层${vectorLayer.title}：图层宽高大于当前画布尺寸2倍`, PsdParseWarnType.Error);
    }
    if (vectorLayer.width <= 0 || vectorLayer.height <= 0) {
        if (parseNoLimit) {
            return null;
        }
        warnOrErrorCallback?.(`矢量图层${vectorLayer.title}：宽高不能小于0`, PsdParseWarnType.Error);
    }
    vectorData.effect = {
        reflection: [],
        shadow: [],
    };

    vectorData.naturalWidth = vectorLayer.width;
    vectorData.naturalHeight = vectorLayer.height;
    vectorLayer.data = vectorData;

    if (parseLayerCallback) {
        const newLayer = await parseLayerCallback(layer, vectorLayer);
        return newLayer;
    }
    return vectorLayer;
};

const getVectorPath = (layer: any, vectorLayer: any, transDiff = 0) => {
    const {
        vectorMask,
        vectorFill,
        vectorStroke,
        vectorOrigination,
        top = 0,
        bottom = 0,
        left = 0,
        right = 0,
        opacity,
        fillOpacity = 1,
        layerColor,
        effects,
    } = layer;
    const paths = vectorMask?.paths,
        isRectVector = isRect(layer),
        isEllipseVector = isEllipse(layer);
    //svg 边框
    let calcPathStrokeWidth = 0; // 用于计算path的strokeWidth
    const borderColor = vectorStroke?.content?.color;
    const borderOpacity = vectorStroke?.opacity;
    const strokeWidth = (() => {
        if (effects?.stroke && effects?.stroke[0] && effects.stroke[0].enabled) {
            calcPathStrokeWidth = Math.ceil(effects.stroke[0].size.value);
            return Math.ceil(effects.stroke[0].size.value);
        } else if (vectorStroke && vectorStroke?.lineWidth?.value) {
            calcPathStrokeWidth = Math.ceil(vectorStroke?.lineWidth?.value) || 0;
            if (vectorStroke?.strokeEnabled) {
                return Math.ceil(vectorStroke?.lineWidth?.value) || 0;
            }
        }
        return 0;
    })();
    let pathString = '',
        fillColor = ``,
        gradientStr = ``,
        borderStr = ``;

    //纯色填充
    if (!vectorFill) {
        if (!vectorStroke) {
            // 特殊情况：没有vectorFill vectorStroke时，默认填充白色
            fillColor = `fill="rgba(255,255,255,1)"`;
        } else {
            fillColor = `fill="none"`;
        }
    } else if (vectorFill && vectorStroke && vectorStroke.fillEnabled === false) {
        fillColor = `fill="none"`;
    } else if (vectorFill.color) {
        //纯色填充
        const { r, g, b } = vectorFill.color;
        if (r === undefined || g === undefined || b === undefined) {
            fillColor = `fill="rgba(255,255,255,${fillOpacity})"`;
        } else {
            fillColor = `fill="rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${fillOpacity})"`;
        }
    } else {
        //渐变填充
        fillColor = `fill="rgba(0,0,0,1)"`;
    }

    if (vectorStroke && vectorStroke?.lineWidth?.value && vectorStroke?.strokeEnabled) {
        const { r, g, b } = borderColor || { r: 0, g: 0, b: 0 };
        const dashSet = vectorStroke.lineDashSet.map((item: any) => item.value * strokeWidth);
        borderStr = `stroke="rgba(${Math.round(r)},${Math.round(g)},${Math.round(
            b,
        )},${borderOpacity})" stroke-width="${strokeWidth}" stroke-dasharray="${dashSet.join()}" stroke-linecap="${
            vectorStroke.lineCapType
        }" stroke-linejoin="${vectorStroke.lineJoinType}" `;
    } else if (effects?.stroke && effects?.stroke[0] && effects.stroke[0].enabled) {
        const { r, g, b } = effects.stroke[0].color;
        const { opacity, size } = effects.stroke[0];

        borderStr = `stroke="rgba(${Math.round(r)},${Math.round(
            g,
        )},${Math.round(b)},${opacity})" stroke-width="${size.value}"  `;
    }

    // 1. 矩形 2. 圆角矩形 5. 椭圆/圆
    if (isRectVector) {
        const keyDescriptorList = vectorOrigination?.keyDescriptorList;
        const keyOriginType = keyDescriptorList[0]?.keyOriginType;
        const boundingBox = keyDescriptorList[0]?.keyOriginShapeBoundingBox;
        // if(boundingBox && boundingBox.left && boundingBox.top){
        //     vectorLayer.left = Math.floor(boundingBox.left?.value);
        //     vectorLayer.top = Math.floor(boundingBox.top?.value);
        // }
        const radius = keyOriginType === 2 ? keyDescriptorList[0]?.keyOriginRRectRadii?.topRight?.value : 0;
        pathString += `<rect x="0" y="0" width="${vectorLayer.width}" height="${vectorLayer.height}" ${
            radius ? `rx="${radius}" ry="${radius}"` : ``
        }  ${fillColor} ${borderStr} />`;
        return pathString;
    } else if (isEllipseVector) {
        pathString += `<ellipse x="0" y="0" cx="${vectorLayer.width / 2}" cy="${vectorLayer.height / 2}"  rx="${
            vectorLayer.width / 2 - strokeWidth
        }" ry="${vectorLayer.height / 2 - strokeWidth}" ${fillColor} ${borderStr} />`;
        return pathString;
    }

    pathString += `<path d=" `;
    let pathD = '';
    // 默认operation = combine，这个时候将所有path放在同个里
    // psd解析的默认都为简单图形，fillRule 为 nonzero
    for (let j = 0; j < paths.length; j++) {
        const knots = paths[j].knots;
        for (let i = 0; i < knots.length; i++) {
            const points = knots[i].points;
            for (let x = 0; x < points.length; x++) {
                points[x] = points[x] + strokeWidth / 2;
            }
        }
    }

    const allPaths = [] as { pathD: string; operation: string }[];
    for (let j = 0; j < paths.length; j++) {
        const knots = paths[j].knots;
        const open = paths[j].open;
        const operation = paths[j].operation;

        const startPoint = knots[0].points;
        let currentPath = ` M${startPoint[2].toFixed(2)} ${startPoint[3].toFixed(2)} `;

        for (let i = 0; i < knots.length; i++) {
            const points = knots[i].points,
                nextPoints = i < knots.length - 1 ? knots[i + 1].points : startPoint,
                ZStr = i === knots.length - 1 ? ` Z ` : ' ';

            if (i < knots.length - 1 || (i === knots.length - 1 && !open)) {
                currentPath += ` C ${points[4].toFixed(2)},${points[5].toFixed(2)} ${nextPoints[0].toFixed(2)}, ${nextPoints[1].toFixed(2)} ${nextPoints[2].toFixed(2)},${nextPoints[3].toFixed(2)} ${ZStr}`;
            }
        }

        allPaths.push({ pathD: currentPath, operation });
    }

    pathD = allPaths.map((item) => item.pathD).join(' ');
    paperCanvas.clear();
    // console.log('pathD',- Math.ceil(calcPathStrokeWidth / 2),  0 - left - Math.ceil(calcPathStrokeWidth / 2), 0 - top - Math.ceil(calcPathStrokeWidth / 2));
    pathD = svgPath(pathD)
        .translate(0 - left, 0 - top)
        // .scale((vectorLayer.width + Math.ceil(strokeWidth/2)) / vectorLayer.width)
        // .round(2)
        .toString();
    // 将pathD 平移transDiff
    pathD = svgPath(pathD).translate(transDiff, transDiff).toString();
    if (calcPathStrokeWidth) {
        vectorLayer.width = vectorLayer.width + calcPathStrokeWidth * 2;
        vectorLayer.height = vectorLayer.height + calcPathStrokeWidth * 2;
    }
    pathString += `${pathD} " ${fillColor} ${borderStr} fill-rule="evenodd" />`;

    return pathString;
};
